import { FormEvent, useEffect, useRef, useState } from "react";
import { isAuthenticated, startLogin } from "../auth";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { createItem, fetchItem, updateItem } from "../api";
import { Category, ShoppingListItem } from "../types";
import { sanitizeReferences, validateItemPayload } from "../utils/validation";

const categories: Category[] = ["toy", "clothing", "consumable", "home"];

export default function ItemFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submitRequested = searchParams.get("submit") === "true";
  const authed = isAuthenticated();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [formState, setFormState] = useState(() => {
    const prefill = {
      name: searchParams.get("name") ?? "",
      category: searchParams.get("category") ?? "toy",
      description: searchParams.get("description") ?? "",
      urls: searchParams.get("url") ? [searchParams.get("url") ?? ""] : [],
      references: searchParams.get("references") ?? "",
      quantity: Number(searchParams.get("quantity") ?? 1) || 1,
      got: false
    };
    return prefill;
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const autoSubmitStarted = useRef(false);

  useEffect(() => {
    if (authed) return;
    setError("Please sign in to add items.");
    startLogin();
  }, [authed]);

  useEffect(() => {
    if (!id) {
      setHint("Creating a fresh item");
      return;
    }
    setLoading(true);
    fetchItem(id)
      .then((item) => {
        setFormState({
          name: item.name,
          category: item.category,
          description: item.description ?? "",
          urls: Array.isArray(item.urls) ? item.urls : item.url ? [item.url] : [],
          references: item.reference.join(", "),
          quantity: item.quantity,
          got: item.got
        });
        setHint("Editing existing item");
      })
      .catch((err) => setError(err.message ?? "Unable to load"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (field: keyof typeof formState, value: string | number | boolean | string[]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    const sanitizedUrls = (formState.urls ?? [])
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    return {
      category: formState.category as Category,
      name: formState.name,
      description: formState.description,
      urls: sanitizedUrls,
      url: sanitizedUrls[0],
      quantity: Number(formState.quantity),
      got: formState.got,
      reference: sanitizeReferences(formState.references)
    };
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationErrors([]);
    setError(null);

    const payload = buildPayload();

    const validation = validateItemPayload(payload);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    try {
      setLoading(true);
      if (id) {
        await updateItem(id, payload);
      } else {
        await createItem(payload);
      }
      navigate("/");
    } catch (err) {
      setError((err as Error).message || "Unable to save");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id || !submitRequested || autoSubmitStarted.current || !authed) return;
    autoSubmitStarted.current = true;

    const payload = buildPayload();
    const validation = validateItemPayload(payload);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      setHint("Quick add could not submit; please fix the fields and try again.");
      return;
    }

    setHint("Submitting from quick add…");
    setLoading(true);

    createItem(payload)
      .then(() => {
        setHint("Saved from quick add.");
        try {
          window.close();
        } catch {
          // Ignore if the browser blocks closing.
        }
      })
      .catch((err) => setError((err as Error).message || "Unable to save"))
      .finally(() => setLoading(false));
  }, [id, submitRequested, authed, formState]);

  return (
    <div className="frosted-card">
      {loading && <p>Loading…</p>}
      <div className="page-actions">
        <Link to="/" className="link-button">
          ← Back to list
        </Link>
      </div>
      <h2>{id ? "Edit item" : "Create item"}</h2>
      {hint && <p>{hint}</p>}
      {validationErrors.length > 0 && (
        <div className="error">
          <p>Fix these before continuing:</p>
          <ul>
            {validationErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <form onSubmit={handleSubmit}>
        <label>
          Name
          <input
            value={formState.name}
            onChange={(event) => handleChange("name", event.target.value)}
            maxLength={256}
            required
          />
        </label>
        <label>
          Category
          <select value={formState.category} onChange={(event) => handleChange("category", event.target.value)}>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </label>
        <label>
          Quantity
          <input
            type="number"
            min={0}
            value={formState.quantity}
            onChange={(event) => handleChange("quantity", Number(event.target.value))}
            required
          />
        </label>
        <label>
          URLs (one per line)
          <textarea
            rows={3}
            value={(formState.urls ?? []).join("\n")}
            onChange={(event) => handleChange("urls", event.target.value.split(/\n+/))}
            maxLength={4096}
            placeholder="https://example.com/product/123"
          />
        </label>
        <label>
          Description
          <textarea
            rows={4}
            value={formState.description}
            onChange={(event) => handleChange("description", event.target.value)}
            maxLength={16384}
          />
        </label>
        <label>
          References (one per line)
          <textarea
            rows={2}
            value={formState.references}
            onChange={(event) => handleChange("references", event.target.value)}
            placeholder={"https://example.com/source\nhttps://another-site.com/page"}
          />
        </label>
        <label>
          Got it?
          <input
            type="checkbox"
            checked={formState.got}
            onChange={(event) => handleChange("got", event.target.checked)}
          />
        </label>
        <div className="page-actions">
          <button type="submit" className="primary" disabled={loading}>
            {id ? "Update item" : "Save item"}
          </button>
        </div>
      </form>
    </div>
  );
}
